import { useState } from 'react'
import type { InteractionProps } from './registry'
import { getNonEmptyString, getStringList } from './shared/config'
import { asRecord } from './shared/response'
import { Feedback } from './shared/Feedback'
import { StaticFallback } from './shared/StaticFallback'
import controls from './shared/controls.module.css'
import styles from './SortItems.module.css'

interface SortItem {
  label: string
  correct: string
}

const UNSORTED = 'Unsorted'

/**
 * Classify items into categories. Every item can be placed by pointer (category
 * chips) or by keyboard-friendly move controls that step it through the
 * categories — no drag required. Immediate, explanatory feedback on check.
 */
export function SortItems({
  config,
  savedResponse,
  onResponseChange,
  onComplete,
}: InteractionProps) {
  const prompt = getNonEmptyString(config, 'prompt')
  const categories = getStringList(config, 'categories')
  const rawItems = getStringList(config, 'items')

  const items = parseItems(rawItems)
  const valid = !!(
    categories &&
    categories.length >= 2 &&
    items.length > 0 &&
    items.every((item) => categories.includes(item.correct))
  )

  const saved = asRecord(savedResponse)
  const [assignments, setAssignments] = useState<Record<string, string>>(() => {
    const savedAssignments = asRecord(saved.assignments)
    const initial: Record<string, string> = {}
    for (const item of items) {
      const value = savedAssignments[item.label]
      initial[item.label] = typeof value === 'string' ? value : UNSORTED
    }
    return initial
  })
  const [checked, setChecked] = useState<boolean>(saved.checked === true)

  if (!valid || !categories) {
    return (
      <StaticFallback title={prompt ?? 'Sort the items'}>
        {items.length > 0 ? (
          <ul>
            {items.map((item) => (
              <li key={item.label}>
                {item.label} — {item.correct}
              </li>
            ))}
          </ul>
        ) : (
          <p>This activity is unavailable.</p>
        )}
      </StaticFallback>
    )
  }

  // Category order for the move controls, with "Unsorted" first.
  const order = [UNSORTED, ...categories]

  function assign(label: string, category: string) {
    const next = { ...assignments, [label]: category }
    setAssignments(next)
    setChecked(false)
    onResponseChange?.({ assignments: next, checked: false })
  }

  function moveBy(label: string, delta: number) {
    const current = order.indexOf(assignments[label] ?? UNSORTED)
    const nextIndex = Math.min(Math.max(current + delta, 0), order.length - 1)
    assign(label, order[nextIndex])
  }

  function check() {
    setChecked(true)
    onResponseChange?.({ assignments, checked: true })
    if (items.every((item) => assignments[item.label] === item.correct)) {
      onComplete?.()
    }
  }

  const correctCount = items.filter((item) => assignments[item.label] === item.correct).length
  const allCorrect = correctCount === items.length

  return (
    <section className={controls.interaction} aria-label={prompt ?? 'Sort the items'}>
      {prompt && <p className={controls.prompt}>{prompt}</p>}

      <ul className={styles.list}>
        {items.map((item) => {
          const placed = assignments[item.label] ?? UNSORTED
          const rowState = checked
            ? assignments[item.label] === item.correct
              ? styles.rowCorrect
              : styles.rowWrong
            : ''
          return (
            <li key={item.label} className={`${styles.row} ${rowState}`}>
              <div className={styles.rowHead}>
                <span className={styles.label}>{item.label}</span>
                <span className={styles.current}>
                  {placed}
                  {checked && (
                    <span className={styles.mark} aria-hidden="true">
                      {assignments[item.label] === item.correct ? ' ✓' : ' ✗'}
                    </span>
                  )}
                </span>
              </div>

              <div className={styles.chips} role="group" aria-label={`Category for ${item.label}`}>
                <button
                  type="button"
                  className={styles.move}
                  onClick={() => moveBy(item.label, -1)}
                  disabled={placed === order[0]}
                  aria-label={`Move ${item.label} up a category`}
                >
                  ↑
                </button>
                {categories.map((category) => (
                  <button
                    key={category}
                    type="button"
                    className={`${styles.chip} ${placed === category ? styles.chipActive : ''}`}
                    aria-pressed={placed === category}
                    onClick={() => assign(item.label, category)}
                  >
                    {category}
                  </button>
                ))}
                <button
                  type="button"
                  className={styles.move}
                  onClick={() => moveBy(item.label, 1)}
                  disabled={placed === order[order.length - 1]}
                  aria-label={`Move ${item.label} down a category`}
                >
                  ↓
                </button>
              </div>
            </li>
          )
        })}
      </ul>

      <div className={controls.actions}>
        <button
          type="button"
          className={`${controls.button} ${controls.buttonPrimary}`}
          onClick={check}
        >
          Check
        </button>
      </div>

      {checked && (
        <Feedback tone={allCorrect ? 'correct' : 'info'}>
          {allCorrect ? (
            <strong>All placed correctly.</strong>
          ) : (
            <>
              You placed {correctCount} of {items.length} correctly. Adjust the ones marked ✗ and
              check again.
            </>
          )}
        </Feedback>
      )}
    </section>
  )
}

/** Parse `"Label | Category"` item strings; drop malformed entries. */
function parseItems(raw: string[] | undefined): SortItem[] {
  if (!raw) return []
  const items: SortItem[] = []
  for (const entry of raw) {
    const pipe = entry.indexOf('|')
    if (pipe === -1) continue
    const label = entry.slice(0, pipe).trim()
    const correct = entry.slice(pipe + 1).trim()
    if (label && correct) items.push({ label, correct })
  }
  return items
}
